/**
 * Product Variants
 * Handles dynamic variant-related updates on the PDP:
 *  - ProductDimensions: updates Size and Weight when the variant changes
 *  - ComplementaryCarousel: updates the complementary products carousel when the variant changes
 */

/**
 * Allow same-origin paths and typical Shopify CDN URLs for complementary cards.
 * @param {unknown} url
 * @returns {string|null}
 */
function safeComplementaryUrl(url) {
  if (url == null || typeof url !== 'string') return null;
  const t = url.trim();
  if (!t) return null;
  if (/^(javascript|data|vbscript):/i.test(t)) return null;
  if (t.startsWith('/') && !t.startsWith('//')) return t;
  try {
    const u = new URL(t, window.location.origin);
    const h = u.hostname.toLowerCase();
    if (u.origin === window.location.origin) {
      return u.pathname + u.search + u.hash;
    }
    if (h.includes('shopifycdn') || h.endsWith('.shopify.com')) {
      return u.href;
    }
  } catch {
    return null;
  }
  return null;
}

/** Known weight units: `other` is used when dimensions.weight > 1 (else `one`). Unknown units pass through unchanged. */
const WEIGHT_UNIT_FORMS = {
  lb: { one: 'lb', other: 'lbs' },
  lbs: { one: 'lb', other: 'lbs' },
  oz: { one: 'oz', other: 'oz' },
  ounce: { one: 'ounce', other: 'ounces' },
  ounces: { one: 'ounce', other: 'ounces' },
  kg: { one: 'kg', other: 'kg' },
  g: { one: 'g', other: 'g' },
  gram: { one: 'gram', other: 'grams' },
  grams: { one: 'gram', other: 'grams' },
};

function displayWeightUnit(rawUnit, weight) {
  const key = String(rawUnit || 'lb')
    .trim()
    .toLowerCase();
  const forms = WEIGHT_UNIT_FORMS[key];
  if (forms) {
    return weight > 1 ? forms.other : forms.one;
  }
  const fallback = String(rawUnit || 'lb').trim();
  return fallback || 'lb';
}

/* ─── Product Dimensions ─────────────────────────────────────────────────── */

if (!customElements.get('product-dimensions')) {
  customElements.define(
    'product-dimensions',
    class ProductDimensions extends HTMLElement {
      constructor() {
        super();
        this.dimensionsData = null;
        this.initializeDimensionsData();
      }

      connectedCallback() {
        if (!this.dimensionsData) {
          this.initializeDimensionsData();
        }

        this.addEventListener('variant-change', this.onVariantChange.bind(this));

        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.handleVariantChangeEvent.bind(this)
          );
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      initializeDimensionsData() {
        const dataScript = document.querySelector('[data-variants-dimensions]');
        if (dataScript) {
          try {
            this.dimensionsData = JSON.parse(dataScript.textContent);
          } catch (e) {
            console.error('Failed to parse variants dimensions data', e);
          }
        }
      }

      handleVariantChangeEvent({ data }) {
        if (!data || !data.variant) return;
        this.updateDimensions(data.variant.id);
      }

      onVariantChange(event) {
        if (event.detail && event.detail.variant) {
          this.updateDimensions(event.detail.variant.id);
        }
      }

      updateDimensions(variantId) {
        if (!this.dimensionsData || !this.dimensionsData[variantId]) return;
        const dimensions = this.dimensionsData[variantId];
        this.updateSize(dimensions);
        this.updateWeight(dimensions);
      }

      formatDimension(value) {
        return (value % 1 === 0) ? Math.floor(value) : value;
      }

      /** Treat 0 / 0.0 / non-numeric as “no size” (match Liquid product-dimensions). */
      isPositiveDimension(value) {
        const n = Number(value);
        return Number.isFinite(n) && n > 0;
      }

      updateSize(dimensions) {
        const sizeContainer = this.querySelector('[data-dimension-size]');
        if (!sizeContainer) return;
        const sizeValue = sizeContainer.querySelector('[data-dimension-value]');
        if (!sizeValue) return;

        const items = [];
        if (this.isPositiveDimension(dimensions.width)) {
          const span = document.createElement('span');
          span.setAttribute('data-width', '');
          span.textContent = `${this.formatDimension(dimensions.width)}"`;
          items.push({ el: span, label: 'W' });
        }
        if (this.isPositiveDimension(dimensions.height)) {
          const span = document.createElement('span');
          span.setAttribute('data-height', '');
          span.textContent = `${this.formatDimension(dimensions.height)}"`;
          items.push({ el: span, label: 'H' });
        }
        if (this.isPositiveDimension(dimensions.thickness)) {
          const span = document.createElement('span');
          span.setAttribute('data-thickness', '');
          span.textContent = `${this.formatDimension(dimensions.thickness)}"`;
          items.push({ el: span, label: 'D' });
        }

        if (items.length > 0) {
          const fragment = document.createDocumentFragment();
          items.forEach(({ el, label }, i) => {
            if (i > 0) fragment.appendChild(document.createTextNode(' x '));
            fragment.appendChild(el);
            fragment.appendChild(document.createTextNode(` ${label}`));
          });
          sizeValue.replaceChildren(fragment);
          sizeContainer.style.display = '';
        } else {
          sizeContainer.style.display = 'none';
        }
      }

      updateWeight(dimensions) {
        const weightContainer = this.querySelector('[data-dimension-weight]');
        if (!weightContainer) return;
        const weightValue = weightContainer.querySelector('[data-weight-value]');
        if (!weightValue) return;

        if (dimensions.weight && dimensions.weight > 0) {
          let weightFormatted = dimensions.weight;
          if (dimensions.weight % 1 === 0) {
            weightFormatted = Math.floor(dimensions.weight);
          }

          const weightUnit = displayWeightUnit(dimensions.weight_unit, dimensions.weight);

          const weightSpan = document.createElement('span');
          weightSpan.setAttribute('data-weight', '');
          weightSpan.textContent = String(weightFormatted);
          weightValue.replaceChildren(weightSpan, document.createTextNode(` ${weightUnit}`));
          weightContainer.style.display = '';
        } else {
          weightContainer.style.display = 'none';
        }
      }
    }
  );
}

/* ─── Complementary Carousel ─────────────────────────────────────────────── */

function complementarySwiperConfig(prevEl, nextEl) {
  return {
    slidesPerView: 1.2,
    spaceBetween: 16,
    navigation: { prevEl, nextEl },
    breakpoints: {
      768: { slidesPerView: 2, spaceBetween: 24 },
      1024: { slidesPerView: 3, spaceBetween: 24 },
    },
  };
}

class ComplementaryCarousel {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.sectionId = wrapper.dataset.sectionId;
    this.swiper = null;
    this._variantUnsubscribe = null;
    this._subscriptionRetryTimer = null;
    this._detachObserver = null;
    this._destroyed = false;
    this.initSwiper();
    this.initSubscription();
    this.syncInitialVariant();
    this._installDetachGuard();
  }

  initSwiper() {
    if (this._destroyed) return;
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.initSwiper(), 100);
      return;
    }
    const swiperEl = this.wrapper.querySelector('[data-complementary-slider]');
    if (!swiperEl) return;
    this.swiper = new Swiper(
      swiperEl,
      complementarySwiperConfig(
        `.${this.sectionId}-comp-prev`,
        `.${this.sectionId}-comp-next`
      )
    );
  }

  _installDetachGuard() {
    if (this._destroyed || !this.wrapper.isConnected) return;
    const parent = this.wrapper.parentElement;
    if (!parent) return;
    this._detachObserver = new MutationObserver(() => {
      if (!this.wrapper.isConnected) {
        this.destroy();
      }
    });
    this._detachObserver.observe(parent, { childList: true });
  }

  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;
    if (this._subscriptionRetryTimer != null) {
      clearTimeout(this._subscriptionRetryTimer);
      this._subscriptionRetryTimer = null;
    }
    if (this._variantUnsubscribe) {
      this._variantUnsubscribe();
      this._variantUnsubscribe = null;
    }
    if (this._detachObserver) {
      this._detachObserver.disconnect();
      this._detachObserver = null;
    }
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  initSubscription() {
    if (this._destroyed) return;
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      if (this._variantUnsubscribe) {
        this._variantUnsubscribe();
        this._variantUnsubscribe = null;
      }
      this._variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (this._destroyed) return;
        if (!data?.variant) return;
        if (String(data.sectionId) !== String(this.sectionId)) return;
        this.updateCarousel(data.variant.id);
      });
    } else {
      this._subscriptionRetryTimer = setTimeout(() => this.initSubscription(), 100);
    }
  }

  syncInitialVariant() {
    let productInfo = this.wrapper.closest('product-info');
    if (!productInfo && this.sectionId) {
      productInfo = document.querySelector(
        `product-info[data-section="${this.sectionId}"]`,
      );
    }
    const input =
      productInfo?.querySelector('form[id^="product-form-"] input[name="id"]') ||
      productInfo?.querySelector('input[name="id"]');
    const raw = input?.value;
    if (!raw) return;
    const id = parseInt(raw, 10);
    if (Number.isNaN(id)) return;
    requestAnimationFrame(() => this.updateCarousel(id));
  }

  buildSlide(item) {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide complementary-slide';

    const link = document.createElement('a');
    link.className = 'complementary-card';
    const href = safeComplementaryUrl(item.url);
    link.setAttribute('href', href || '#');

    const imageWrap = document.createElement('div');
    imageWrap.className = 'complementary-card__image';

    const imgSrc = safeComplementaryUrl(item.image);
    if (imgSrc) {
      const img = document.createElement('img');
      img.setAttribute('src', imgSrc);
      img.setAttribute('alt', item.image_alt != null ? String(item.image_alt) : '');
      img.setAttribute('loading', 'lazy');
      img.setAttribute('width', '400');
      if (item.image_height != null && item.image_height !== '') {
        img.setAttribute('height', String(item.image_height));
      }
      imageWrap.appendChild(img);
    } else {
      const ph = document.createElement('span');
      ph.className = 'placeholder-svg';
      imageWrap.appendChild(ph);
    }

    const content = document.createElement('div');
    content.className = 'complementary-card__content';

    const titleEl = document.createElement('h3');
    titleEl.className = 'complementary-card__title';
    titleEl.textContent = item.title != null ? String(item.title) : '';

    const skuEl = document.createElement('p');
    skuEl.className = 'product__sku';
    const sku = item.sku != null ? String(item.sku).trim() : '';
    if (!sku) {
      skuEl.classList.add('visibility-hidden');
    }
    const skuLabel = document.createElement('span');
    skuLabel.className = 'product__sku-label';
    skuLabel.textContent = 'Model # ';
    skuEl.append(skuLabel, document.createTextNode(sku));

    const priceWrap = document.createElement('div');
    priceWrap.className = 'complementary-card__price';

    const priceStr = item.price != null ? String(item.price) : '';
    const onSale = Boolean(item.on_sale && item.compare_at_price);

    if (item.price_varies) {
      const fromSpan = document.createElement('span');
      fromSpan.className = 'price-from';
      fromSpan.textContent = 'From ';
      priceWrap.appendChild(fromSpan);
    }

    if (onSale) {
      const compareSpan = document.createElement('span');
      compareSpan.className = 'complementary-card__price--compare';
      compareSpan.textContent = String(item.compare_at_price);
      const saleSpan = document.createElement('span');
      saleSpan.className = 'complementary-card__price--sale';
      saleSpan.textContent = priceStr;
      priceWrap.append(compareSpan, saleSpan);
    } else {
      const priceSpan = document.createElement('span');
      priceSpan.className = 'price';
      priceSpan.textContent = priceStr;
      priceWrap.appendChild(priceSpan);
    }

    content.append(titleEl, skuEl, priceWrap);
    link.append(imageWrap, content);
    slide.appendChild(link);
    return slide;
  }

  updateCarousel(variantId) {
    if (this._destroyed) return;
    const dataScript = document.getElementById(`complementary-variants-data-${this.sectionId}`);
    if (!dataScript) return;

    let variantsData;
    try { variantsData = JSON.parse(dataScript.textContent); } catch { return; }

    const key = variantId != null ? String(variantId) : '';
    const products =
      variantsData[key] ?? variantsData[variantId] ?? [];

    if (!products || products.length === 0) {
      this.wrapper.style.display = 'none';
      return;
    }

    this.wrapper.style.display = '';

    const swiperWrapper = this.wrapper.querySelector('[data-complementary-slider] .swiper-wrapper');
    if (swiperWrapper) {
      swiperWrapper.replaceChildren(...products.map((item) => this.buildSlide(item)));
    }

    if (this.swiper) {
      this.swiper.update();
      this.swiper.slideTo(0, 0);
    }
  }
}

/* ─── Init ───────────────────────────────────────────────────────────────── */

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-complementary-wrapper]').forEach((wrapper) => {
    new ComplementaryCarousel(wrapper);
  });

  // Init Swiper for the static product-recommendations fallback after AJAX load
  const productRecsEl = document.querySelector('product-recommendations.complementary-products');
  if (productRecsEl) {
    const tryInitStaticSwiper = () => {
      const swiperEl = productRecsEl.querySelector('[data-complementary-slider]');
      if (!swiperEl || swiperEl._swiperInitialized) return;
      if (typeof Swiper === 'undefined') { setTimeout(tryInitStaticSwiper, 100); return; }
      swiperEl._swiperInitialized = true;
      const sectionId = productRecsEl.dataset.sectionId;
      new Swiper(swiperEl, complementarySwiperConfig(
        `.${sectionId}-comp-prev`,
        `.${sectionId}-comp-next`
      ));
    };
    const observer = new MutationObserver(tryInitStaticSwiper);
    observer.observe(productRecsEl, { childList: true });
  }
});

/* ─── Design Project Block ─────────────────────────────────────────────────── */

if (!customElements.get('design-project-block')) {
  customElements.define(
    'design-project-block',
    class DesignProjectBlock extends HTMLElement {
      constructor() {
        super();
        this.variantsData = null;
      }

      connectedCallback() {
        this.initializeVariantsData();
        this.updateVisibility();

        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.handleVariantChange.bind(this)
          );
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      initializeVariantsData() {
        const dataScript = document.querySelector('[data-product-variants]');
        if (dataScript) {
          try {
            this.variantsData = JSON.parse(dataScript.textContent);
          } catch (e) {
            console.error('Failed to parse variants data', e);
          }
        }
      }

      handleVariantChange({ data }) {
        if (!data || !data.variant) return;
        this.updateVisibility(data.variant.id);
      }

      updateVisibility(variantId = null) {
        if (!this.variantsData) return;

        let currentVariant;
        if (variantId) {
          currentVariant = this.variantsData.find((v) => v.id == variantId);
        } else {
          const selectedVariantScript = document.querySelector('[data-selected-variant]');
          if (selectedVariantScript) {
            try {
              currentVariant = JSON.parse(selectedVariantScript.textContent);
            } catch (e) {
              console.error('[DesignProjectBlock] Error parsing variant:', e);
              return;
            }
          }
        }

        if (!currentVariant) return;

        const cfg = currentVariant.configurator_product;
        if (cfg === true || cfg === 'true' || cfg === 1) {
          this.classList.remove('hidden');
        } else {
          this.classList.add('hidden');
        }
      }
    }
  );
}
